/**
 * 供應商確認 Vendor Confirmations lens (S4.3, ux-lens finding 5).
 *
 * READ-ONLY A1/A2/A3 status per PO line, projected from the same allocation
 * facts the PO checkpoint sections read. The three checkpoints ALWAYS render
 * as the sanctioned bilingual labels from `ofcVocabulary.ts` (「A1 估計」…) —
 * bare A-codes live in code only. OCC hosts NO write drawer: the 80px action
 * drills to the PO detail where `VendorConfirmDrawer` lives.
 */

import { useMemo } from 'react';
import { Button, Table, Tag, theme } from 'antd';
import type { TableProps } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import { useOccBoard } from '@/hooks/useOcc';
import { useCheckpointLabel } from '@/components/procurement/PurchaseOrderDetailPage/checkpoints';
import { LensFooter, LensUnavailableNotice, ReadOnlyBanner } from '../lensShared';

/** One read-only row per PO line (allocation) with its three checkpoint facts. */
interface VendorConfirmRow {
  key: string;
  poId: string;
  poNumber: string;
  vendorName: string;
  sku: string;
  productName: string;
  qtyPo: number;
  qtyEstimated: number;
  qtyNoticeConfirmed: number;
  qtyLoaded: number;
}

const VendorConfirmLens = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['occ', 'common']);
  const { token } = theme.useToken();
  const cpLabel = useCheckpointLabel();

  const { legacyLines, syncedAt, isLoading, error, refresh, unavailable } = useOccBoard();

  const rows: VendorConfirmRow[] = useMemo(
    () =>
      legacyLines.flatMap((line) =>
        line.allocations.map((alloc) => ({
          key: alloc.id,
          poId: alloc.poId,
          poNumber: alloc.poNumber,
          vendorName: alloc.vendorName,
          sku: line.sku,
          productName: line.productName,
          qtyPo: alloc.qtyPo,
          qtyEstimated: alloc.qtyEstimated,
          qtyNoticeConfirmed: alloc.qtyNoticeConfirmed,
          qtyLoaded: alloc.qtyLoaded,
        }))
      ),
    [legacyLines]
  );

  /** Recorded → green tag with the qty (number = non-colour channel); else '-'. */
  const renderCheckpoint = (qty: number) =>
    qty > 0 ? (
      <Tag color="green" style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}>
        {qty}
      </Tag>
    ) : (
      renderDash()
    );

  const columns: TableProps<VendorConfirmRow>['columns'] = [
    {
      title: t('occ:occ.vendorConfirm.columns.po'),
      key: 'po',
      width: 220,
      render: (_: unknown, row) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>{row.poNumber}</span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {row.vendorName}
          </span>
        </div>
      ),
    },
    {
      title: t('occ:occ.vendorConfirm.columns.product'),
      key: 'product',
      width: 220,
      render: (_: unknown, row) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span>{row.sku}</span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {row.productName}
          </span>
        </div>
      ),
    },
    {
      title: t('occ:occ.vendorConfirm.columns.ordered'),
      dataIndex: 'qtyPo',
      key: 'qtyPo',
      width: 100,
      align: 'right',
      render: (value: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>,
    },
    // The three checkpoints — bilingual labels from ofcVocabulary, never bare codes.
    {
      title: cpLabel('A1'),
      key: 'a1',
      width: 130,
      align: 'right',
      render: (_: unknown, row) => renderCheckpoint(row.qtyEstimated),
    },
    {
      title: cpLabel('A2'),
      key: 'a2',
      width: 170,
      align: 'right',
      render: (_: unknown, row) => renderCheckpoint(row.qtyNoticeConfirmed),
    },
    {
      title: cpLabel('A3'),
      key: 'a3',
      width: 130,
      align: 'right',
      render: (_: unknown, row) => renderCheckpoint(row.qtyLoaded),
    },
    {
      title: t('occ:occ.vendorConfirm.columns.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, row) => (
        <Button
          type="text"
          aria-label={t('occ:occ.vendorConfirm.drill')}
          icon={<ExportOutlined style={{ color: token.colorPrimary }} />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/auth/purchase-orders/${row.poId}`);
          }}
        />
      ),
    },
  ];

  return (
    <>
      <LensUnavailableNotice active={unavailable} domain="vendorConfirm" />
      <ReadOnlyBanner note={t('occ:occ.vendorConfirm.readOnlyNote')} />
      <TableContainer
        errorState={{
          active: Boolean(error),
          title: t('occ:occ.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<VendorConfirmRow>
          rowKey="key"
          dataSource={rows}
          columns={columns}
          loading={isLoading}
          onRow={(row) => ({
            onClick: () => navigate(`/auth/purchase-orders/${row.poId}`),
            style: { cursor: 'pointer' },
          })}
          locale={{ emptyText: <ListEmptyState title={t('occ:occ.states.noConfirmations')} /> }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 1050 }}
        />
      </TableContainer>
      <LensFooter syncedAt={syncedAt} />
    </>
  );
};

export default VendorConfirmLens;
